
import { useState, useCallback, useRef } from 'react';
import { toError, haversineMeters } from '@parleyride/hooks';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/components/ui/use-toast';
import { useTrafficConditions } from './useTrafficConditions';
import { useRouteRenderer } from './useRouteRenderer';

/**
 * Beyond this, it is not a ride — it is a flight. Checked as a precondition by
 * callers (see `exceedsRideDistance`) so the reason survives: `getRoute`
 * collapses every failure to `null`, which used to turn "these places are
 * 13,784 km apart" into a generic "could not calculate a route".
 */
export const MAX_ROUTE_DISTANCE_KM = 1000;

/** Great-circle distance in km between two `[lng, lat]` pairs. */
export const routeDistanceKm = (
  from: [number, number],
  to: [number, number],
): number =>
  haversineMeters({ lat: from[1], lng: from[0] }, { lat: to[1], lng: to[0] }) / 1000;

/**
 * Whether these two points are too far apart to be a ride. Callers should ask
 * this *before* calling getRoute: it needs no network, and it lets them say
 * why rather than reporting a routing failure they did not have.
 */
export const exceedsRideDistance = (
  from: [number, number],
  to: [number, number],
): number | null => {
  const km = routeDistanceKm(from, to);
  return km > MAX_ROUTE_DISTANCE_KM ? km : null;
};

export interface RouteData {
  geometry: GeoJSON.LineString;
  distance: number; // in meters
  duration: number; // in seconds
  trafficDuration?: number; // in seconds, accounting for traffic
  congestion?: string; // low, moderate, heavy
  eta?: string; // estimated time of arrival
  etaText?: string; // formatted ETA text
}

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

export interface GetRouteOptions {
  /**
   * Whether a failure (or a heavy-traffic warning) raises a toast.
   *
   * Defaults to true, which is right for the interactive call sites: the user
   * asked for a route and deserves to be told it did not come back. Pass
   * `false` from anything on a timer. LiveTripMap re-routes the ETA every 30 s
   * for the whole trip, so a down `location` function or an exhausted
   * Directions quota would otherwise mean a destructive toast every 30 s until
   * the ride ends — and the component already degrades to the straight-line
   * estimate on its own.
   */
  notify?: boolean;
}

// 5 minutes in milliseconds. Module-level so it's a stable reference for
// the `withCache` callback below (no need to list it as a dependency).
const CACHE_TTL = 5 * 60 * 1000;

export const useRouting = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { trafficConditions } = useTrafficConditions();
  const { addRouteToMap } = useRouteRenderer();

  // Create a cache with 5-minute expiration for routes
  const routeCacheRef = useRef<Map<string, CacheItem<RouteData>>>(new Map());

  // Helper function to get/set cache
  const withCache = useCallback(async <T extends RouteData>(key: string, fetchFn: () => Promise<T>): Promise<T> => {
    const now = Date.now();
    const cached = routeCacheRef.current.get(key);
    
    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.data as T;
    }
    
    const data = await fetchFn();
    routeCacheRef.current.set(key, { data, timestamp: now });
    return data;
  }, []);
  
  const getRoute = useCallback(async (
    fromCoords: [number, number], 
    toCoords: [number, number],
    includeTraffic: boolean = true,
    { notify = true }: GetRouteOptions = {}
  ): Promise<RouteData | null> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Backstop for callers that did not check `exceedsRideDistance` first.
      // The reason does not survive the `return null` below, so a caller that
      // wants to *tell* the user why must check before calling.
      const distance = routeDistanceKm(fromCoords, toCoords);
      if (distance > MAX_ROUTE_DISTANCE_KM) {
        throw new Error(`This route is too long (${Math.round(distance)} km). Please choose locations within a reasonable distance for ride-sharing.`);
      }
      
      // Create a cache key based on coordinates and traffic preference
      const cacheKey = `route:${fromCoords[0]},${fromCoords[1]}|${toCoords[0]},${toCoords[1]}|${includeTraffic}`;
      
      return await withCache(cacheKey, async () => {
        const maxRetries = 2; // Reduced retries to prevent spam
        let retryCount = 0;
        let lastError;

        while (retryCount < maxRetries) {
          try {
            const { data, error } = await supabase.functions.invoke('location', {
              body: {
                action: 'getRoute',
                params: {
                  from: fromCoords,
                  to: toCoords,
                  includeTraffic
                }
              }
            });
            
            if (error) {
              throw new Error(error.message || 'Failed to fetch route data');
            }
            
            if (!data || !data.routes || data.routes.length === 0) {
              throw new Error('No route found between these locations');
            }
            
            const route = data.routes[0];
            const congestion = route.congestion_level || 'low';
            const trafficCondition = trafficConditions[congestion];
            
            // Calculate ETA based on current time + duration
            const now = new Date();
            const etaDate = new Date(now.getTime() + (route.duration * 1000));
            const eta = etaDate.toISOString();
            
            // Format ETA text for display
            const hours = etaDate.getHours();
            const minutes = etaDate.getMinutes();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const formattedHours = hours % 12 || 12;
            const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
            const etaText = `${formattedHours}:${formattedMinutes} ${ampm}`;
            
            // Show toast notification for mobile users if traffic is heavy
            if (notify && isMobile && congestion === 'heavy') {
              toast({
                title: "Heavy Traffic Alert",
                description: `There's heavy traffic on your route. ETA: ${etaText}`,
                variant: "destructive"
              });
            }
            
            const routeData: RouteData = {
              geometry: route.geometry,
              distance: route.distance,
              duration: route.duration,
              trafficDuration: route.duration_in_traffic || route.duration,
              congestion,
              eta,
              etaText
            };
            
            return routeData;
          } catch (err) {
            console.error(`Error fetching route (attempt ${retryCount + 1}):`, err);
            lastError = toError(err);
            retryCount++;
            
            // Don't retry on certain error types
            if (lastError.message.includes('Route exceeds maximum distance') ||
                lastError.message.includes('too long') ||
                lastError.message.includes('InvalidInput')) {
              break;
            }
            
            if (retryCount < maxRetries) {
              // Wait before retrying
              const delay = Math.pow(2, retryCount) * 1000;
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }
        
        // If we got here, all retries failed
        throw lastError || new Error('Failed to calculate route after multiple attempts');
      });
    } catch (err) {
      console.error('Error fetching route:', err);
      const message = err instanceof Error ? err.message : 'Failed to calculate route';
      setError(message);

      // Only show toast for unexpected errors, not distance limitations — and
      // never for a caller that opted out (see GetRouteOptions.notify).
      if (
        notify &&
        !message.includes('too long') &&
        !message.includes('Route exceeds maximum distance')
      ) {
        toast({
          title: "Route Error",
          description: message,
          variant: "destructive",
        });
      }
      
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [withCache, trafficConditions, isMobile, toast]);
  
  const clearRouteCache = useCallback(() => {
    routeCacheRef.current.clear();
  }, []);
  
  return {
    getRoute,
    addRouteToMap,
    isLoading,
    error,
    trafficConditions,
    clearRouteCache
  };
};
